// Accepts output of useLiveDataTransform, makes it available to children

import React from 'react';
import UseLiveDataTransform from './use-live-data-transform';
import type { LivePreviewConnectionState, LivePreviewError } from './types';

const Ctx = React.createContext<Record<string, unknown>>({});

const LiveUpdateProvider = <T extends Record<string, unknown>>({
    children,
    data,
    isPreview,
    franchises,
    hasUpdatedFromCs,
    error,
    connectionState,
}: React.PropsWithChildren<ReturnType<typeof UseLiveDataTransform<T>>>): JSX.Element => {
    const value = React.useMemo(
        () => ({
            data,
            isPreview,
            hasUpdatedFromCs,
            franchises,
            error,
            connectionState,
        }),
        [data, isPreview, hasUpdatedFromCs, franchises, error, connectionState],
    );
    return (
        <Ctx.Provider value={value}>
            {children}
        </Ctx.Provider>
    );
};

export default LiveUpdateProvider;

export const useLiveUpdateProvider = <T extends Record<string, unknown>>(
// eslint-disable-next-line @typescript-eslint/no-unused-vars
    data?: T, // This is used for type inference
    preferInput?: boolean,
): ReturnType<typeof UseLiveDataTransform<T>> => {
    const ctxResult = React.useContext(Ctx) as ReturnType<typeof UseLiveDataTransform<T>>;
    // We use preferInput when data may have already been transformed
    // I.e., within a microsite, where variables may have updated the content,
    // and content has already been combined with drafts input in a parent component (i.e., the franchise-page template)
    if (data && preferInput) {
        return {
            ...ctxResult,
            data,
        };
    }
    return ctxResult;
};

export const useLiveUpdateFranchises = <T extends Record<string, unknown>>(): {
    franchises: ReturnType<typeof UseLiveDataTransform<T>>['franchises']
    isPreview: ReturnType<typeof UseLiveDataTransform<T>>['isPreview']
} => {
    const ctxResult = React.useContext(Ctx) as ReturnType<typeof UseLiveDataTransform<T>>;
    if (!ctxResult.hasUpdatedFromCs) {
        return {
            franchises: [],
            isPreview: false,
        };
    }
    return {
        franchises: ctxResult.franchises,
        isPreview: ctxResult.isPreview,
    };
};

/**
 * Exposes live-preview diagnostic state (connection + errors) to any
 * component inside the LiveUpdateProvider tree. Returns null values when
 * not running in live-preview mode.
 */
export const useLiveUpdateStatus = (): {
    error: LivePreviewError | null;
    connectionState: LivePreviewConnectionState;
    isPreview: boolean;
} => {
    const ctxResult = React.useContext(Ctx) as {
        error?: LivePreviewError | null;
        connectionState?: LivePreviewConnectionState;
        isPreview?: boolean;
    };
    return {
        error: ctxResult.error ?? null,
        connectionState: ctxResult.connectionState ?? 'idle',
        isPreview: !!ctxResult.isPreview,
    };
};
